// Bot Manager v2 (PRD §1.3, Part 4, WP-07).
// Multi-number WhatsApp bot management hub with 6 WhatChimp section groups,
// per-number 7-tab Bot Settings drawer, Quick Actions manager, and Options tools.
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { useAccounts, ALL_ACCOUNTS } from '../lib/accounts';
import { cn } from '../lib/cn';
import BotSettingsDrawer from '../components/bots/BotSettingsDrawer';
import QuickActionsView from '../components/bots/QuickActionsView';
import CustomVariablesModal from '../components/bots/CustomVariablesModal';
import BotErrorLogsModal from '../components/bots/BotErrorLogsModal';
import FlowFoldersModal from '../components/bots/FlowFoldersModal';
import ResetActionsConfirmDialog from '../components/bots/ResetActionsConfirmDialog';
import { numberLabel } from '../lib/whatsappApi';
import { Button } from '../components/ui/Button';
import {
  SettingsIcon,
  FormsIcon,
  AlertTriangleIcon,
  TagIcon,
  RefreshIcon,
  ChevronDownIcon,
  CheckIcon,
} from '../components/icons';

interface HubCard {
  id: string;
  titleKey: string;
  defaultTitle: string;
  descKey: string;
  defaultDesc: string;
  icon: string;
  path?: string;
  isQuickActions?: boolean;
  badge?: string;
}

interface HubGroup {
  id: string;
  titleKey: string;
  defaultTitle: string;
  cards: HubCard[];
}

export default function BotManager() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { numbers, loading: loadingAccounts, defaultId, selectedId, setSelectedId } = useAccounts();

  // Active integration
  const activeIntegration = useMemo(() => {
    if (selectedId && selectedId !== ALL_ACCOUNTS) {
      const match = numbers.find((n) => n.id === selectedId);
      if (match) return match;
    }
    if (defaultId) {
      const match = numbers.find((n) => n.id === defaultId);
      if (match) return match;
    }
    return numbers[0] || null;
  }, [numbers, selectedId, defaultId]);

  const activeIntegrationId = activeIntegration?.id || 'primary';

  // Navigation & view states
  const [activeTab, setActiveTab] = useState<'hub' | 'quickActions'>('hub');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [optionsMenuOpen, setOptionsMenuOpen] = useState(false);

  // Modals
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [errorLogsOpen, setErrorLogsOpen] = useState(false);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const hubGroups: HubGroup[] = [
    {
      id: 'automation',
      titleKey: 'bots.group.automation',
      defaultTitle: 'Core Automation',
      cards: [
        {
          id: 'quickActions',
          titleKey: 'bots.cards.quickActions',
          defaultTitle: 'Quick Actions (System Actions)',
          descKey: 'bots.cards.quickActionsDesc',
          defaultDesc: '9 standard automated triggers: welcome, fallback, location, handoff, order.',
          icon: '⚡',
          isQuickActions: true,
          badge: '9 Actions',
        },
        {
          id: 'flows',
          titleKey: 'bots.cards.flowBuilder',
          defaultTitle: 'Visual Flow Builder',
          descKey: 'bots.cards.flowBuilderDesc',
          defaultDesc: 'Build multi-step interactive chatbot flows with drag-and-drop canvas.',
          icon: '🔀',
          path: '/flows',
        },
        {
          id: 'replies',
          titleKey: 'bots.cards.keywordReplies',
          defaultTitle: 'Keyword Auto-Replies',
          descKey: 'bots.cards.keywordRepliesDesc',
          defaultDesc: 'Trigger automated messages and workflows on exact or partial keyword matches.',
          icon: '🤖',
          path: '/bot/auto-replies',
        },
        {
          id: 'sequences',
          titleKey: 'bots.cards.sequences',
          defaultTitle: 'Follow-up Sequences',
          descKey: 'bots.cards.sequencesDesc',
          defaultDesc: 'Timed drip campaigns, automated follow-ups, and scheduled nurturing messages.',
          icon: '⏩',
          path: '/bot/sequences',
        },
        {
          id: 'templates',
          titleKey: 'bots.cards.templates',
          defaultTitle: 'Message Templates',
          descKey: 'bots.cards.templatesDesc',
          defaultDesc: 'Synchronized Meta-approved broadcast and conversational notification templates.',
          icon: '🧩',
          path: '/templates',
        },
        {
          id: 'ads',
          titleKey: 'bots.cards.clickAds',
          defaultTitle: 'Click-to-WhatsApp Ads',
          descKey: 'bots.cards.clickAdsDesc',
          defaultDesc: 'Track ad campaigns and trigger instant conversational onboarding from Meta ads.',
          icon: '📢',
          // '/bot/ads' was never a route — it fell through to the catch-all
          // and dumped the user on the dashboard.
          path: '/ads',
        },
      ],
    },
    {
      id: 'data',
      titleKey: 'bots.group.data',
      defaultTitle: 'Data Collection',
      cards: [
        {
          id: 'inputFlows',
          titleKey: 'bots.cards.inputFlows',
          defaultTitle: 'User Input Flows',
          descKey: 'bots.cards.inputFlowsDesc',
          defaultDesc: 'Structured questionnaires capturing email, phone, location, and custom fields.',
          icon: '📝',
          path: '/bot/input-flows',
        },
      ],
    },
    {
      id: 'ai',
      titleKey: 'bots.group.ai',
      defaultTitle: 'AI & Smart Agents',
      cards: [
        {
          id: 'aiAgent',
          titleKey: 'bots.cards.aiAgent',
          defaultTitle: 'AI Configuration & Agents',
          descKey: 'bots.cards.aiAgentDesc',
          defaultDesc: 'Train intelligent conversational agents with knowledge sources and AI models.',
          icon: '✨',
          path: '/bot/ai-agent',
        },
      ],
    },
    {
      id: 'engagement',
      titleKey: 'bots.group.engagement',
      defaultTitle: 'Engagement & Entry Points',
      cards: [
        {
          id: 'widget',
          titleKey: 'bots.cards.chatWidget',
          defaultTitle: 'Chat Widget & QR Codes',
          descKey: 'bots.cards.chatWidgetDesc',
          defaultDesc: 'Embeddable website chat widget, clickable entry links, and QR codes.',
          icon: '💬',
          path: '/bot/chat-widget',
        },
      ],
    },
    {
      id: 'commerce',
      titleKey: 'bots.group.commerce',
      defaultTitle: 'Commerce & Orders',
      cards: [
        {
          id: 'storeAutomation',
          titleKey: 'bots.cards.storeAutomation',
          defaultTitle: 'Store Automation',
          descKey: 'bots.cards.storeAutomationDesc',
          defaultDesc: 'WooCommerce, Shopify, and Salla cart abandonment and COD confirmations.',
          icon: '🛒',
          path: '/store',
        },
        {
          id: 'catalog',
          titleKey: 'bots.cards.catalog',
          defaultTitle: 'Product Catalog',
          descKey: 'bots.cards.catalogDesc',
          defaultDesc: 'Manage items and send native WhatsApp interactive catalog messages.',
          icon: '📦',
          path: '/catalog',
        },
      ],
    },
    {
      id: 'integrations',
      titleKey: 'bots.group.integrations',
      defaultTitle: 'Integrations & Outbound',
      cards: [
        {
          id: 'google',
          titleKey: 'bots.cards.google',
          defaultTitle: 'Google Integration',
          descKey: 'bots.cards.googleDesc',
          defaultDesc: 'Sync contacts, append subscribers to Google Sheets, and create Calendar events.',
          icon: '📊',
          path: '/google-integration',
        },
        {
          id: 'httpApi',
          titleKey: 'bots.cards.httpApi',
          defaultTitle: 'HTTP API (Outbound)',
          descKey: 'bots.cards.httpApiDesc',
          defaultDesc: 'Send outbound web requests and sync webhook responses directly inside bot flows.',
          icon: '📡',
          path: '/http-api',
        },
        {
          id: 'webhooks',
          titleKey: 'bots.cards.webhooks',
          defaultTitle: 'Outbound Webhooks',
          descKey: 'bots.cards.webhooksDesc',
          defaultDesc: 'Stream inbound and status events with per-bot triggers and signature signing.',
          icon: '🔗',
          path: '/settings?tab=webhooks',
        },
      ],
    },
  ];

  const handleCardClick = (card: HubCard) => {
    if (card.isQuickActions) {
      setActiveTab('quickActions');
    } else if (card.path) {
      navigate(card.path);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-ink dark:text-white">
              {t('bots.title') || 'Bot Manager'}
            </h1>
            {activeIntegration && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                {numberLabel(activeIntegration)}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
            {t('bots.subtitle') ||
              'Configure per-number automation rules, system responses, AI agents, and business hours.'}
          </p>
        </div>

        {/* Primary Controls */}
        <div className="flex items-center gap-2.5">
          <Button
            size="sm"
            onClick={() => setSettingsOpen(true)}
            leadingIcon={<SettingsIcon className="h-4 w-4" />}
          >
            {t('bots.settingsBtn') || 'Bot Settings'}
          </Button>

          {/* Options Dropdown */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setOptionsMenuOpen((p) => !p)}
              trailingIcon={<ChevronDownIcon className="h-3.5 w-3.5" />}
            >
              {t('bots.options') || 'Options'}
            </Button>

            {optionsMenuOpen && (
              <>
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  onClick={() => setOptionsMenuOpen(false)}
                  className="fixed inset-0 z-20 cursor-default"
                />
                <div className="absolute end-0 top-full z-30 mt-1.5 w-52 rounded-2xl border border-line bg-white p-1.5 shadow-xl dark:border-slate-800 dark:bg-slate-900 text-xs text-ink-2 dark:text-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsMenuOpen(false);
                      setVariablesOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start hover:bg-surface dark:hover:bg-slate-800 transition-colors"
                  >
                    <TagIcon className="h-4 w-4 text-brand" />
                    <span>{t('bots.options.variables') || 'Custom Variables'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsMenuOpen(false);
                      setErrorLogsOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start hover:bg-surface dark:hover:bg-slate-800 transition-colors"
                  >
                    <AlertTriangleIcon className="h-4 w-4 text-warning" />
                    <span>{t('bots.options.errorLogs') || 'Bot Error Logs'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsMenuOpen(false);
                      setFoldersOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start hover:bg-surface dark:hover:bg-slate-800 transition-colors"
                  >
                    <FormsIcon className="h-4 w-4 text-info" />
                    <span>{t('bots.options.folders') || 'Flow Folders'}</span>
                  </button>
                  <div className="my-1 border-t border-line dark:border-slate-800" />
                  <button
                    type="button"
                    onClick={() => {
                      setOptionsMenuOpen(false);
                      setResetDialogOpen(true);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-start text-danger hover:bg-danger/10 transition-colors"
                  >
                    <RefreshIcon className="h-4 w-4" />
                    <span>{t('bots.options.resetActions') || 'Reset Action Buttons'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Grid: Left Account Selector + Right View */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Left: Accounts / Numbers List */}
        <div className="space-y-3 lg:col-span-1">
          <div className="rounded-2xl border border-line bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
              {t('bots.accountsTitle') || 'Connected WhatsApp Numbers'}
            </h2>
            <div className="mt-3 space-y-2">
              {loadingAccounts ? (
                <div className="py-4 text-center text-xs text-ink-3">{t('bot.loadingNumbers') || 'Loading numbers...'}</div>
              ) : numbers.length === 0 ? (
                <div className="py-4 text-center text-xs text-ink-3">{t('bot.noNumbersConnected') || 'No numbers connected'}</div>
              ) : (
                numbers.map((num) => {
                  const isSelected = num.id === activeIntegrationId;
                  return (
                    <button
                      key={num.id}
                      type="button"
                      onClick={() => setSelectedId(num.id)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-xl p-2.5 text-start transition-all',
                        isSelected
                          ? 'border border-brand/40 bg-brand/5 text-brand dark:border-brand/50 dark:bg-brand/10'
                          : 'border border-transparent bg-surface/50 text-ink-2 hover:bg-surface dark:bg-slate-800/50 dark:text-slate-300 dark:hover:bg-slate-800'
                      )}
                    >
                      <div className="min-w-0 pr-2">
                        <p className="truncate text-xs font-semibold">
                          {numberLabel(num)}
                        </p>
                        {num.label && num.verifiedPhoneE164 && (
                          <p className="truncate text-[11px] text-ink-3 dark:text-slate-400">
                            {num.verifiedPhoneE164}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {defaultId === num.id && (
                          <span className="rounded bg-brand/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand">
                            Default
                          </span>
                        )}
                        {isSelected && <CheckIcon className="h-4 w-4 text-brand" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Quick Stats / Info Widget */}
          <div className="rounded-2xl border border-line bg-surface/40 p-4 dark:border-slate-800 dark:bg-slate-900/40 text-xs space-y-2.5">
            <span className="font-semibold text-ink dark:text-slate-200">
              {t('bots.tipTitle') || 'Automation Tip'}
            </span>
            <p className="text-[11px] text-ink-3 dark:text-slate-400 leading-relaxed">
              {t('bots.tipDesc') ||
                'Business hours and out-of-office away replies are configured per WhatsApp number. Click "Bot Settings" above to adjust schedules and shared inbox behavior.'}
            </p>
          </div>
        </div>

        {/* Right: Tabs & Content */}
        <div className="space-y-5 lg:col-span-3">
          {/* Nav Pills */}
          <div className="flex items-center gap-2 border-b border-line pb-3 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setActiveTab('hub')}
              className={cn(
                'rounded-xl px-4 py-2 text-xs font-bold transition-all',
                activeTab === 'hub'
                  ? 'bg-brand text-white shadow-sm'
                  : 'bg-white text-ink-2 hover:bg-surface dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              )}
            >
              {t('bots.tab.hub') || 'Automation Hub'}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('quickActions')}
              className={cn(
                'rounded-xl px-4 py-2 text-xs font-bold transition-all flex items-center gap-1.5',
                activeTab === 'quickActions'
                  ? 'bg-brand text-white shadow-sm'
                  : 'bg-white text-ink-2 hover:bg-surface dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
              )}
            >
              <span>{t('bots.tab.quickActions') || 'Quick Actions'}</span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.2 text-[10px]',
                  activeTab === 'quickActions' ? 'bg-white/20 text-white' : 'bg-brand/10 text-brand'
                )}
              >
                9
              </span>
            </button>
          </div>

          {/* TAB 1: AUTOMATION HUB */}
          {activeTab === 'hub' && (
            <div className="space-y-6">
              {hubGroups.map((grp) => (
                <div key={grp.id} className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                    {t(grp.titleKey) || grp.defaultTitle}
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {grp.cards.map((card) => {
                      const title = t(card.titleKey) || card.defaultTitle;
                      const desc = t(card.descKey) || card.defaultDesc;
                      const isClickable = Boolean(card.path || card.isQuickActions);

                      return (
                        <button
                          key={card.id}
                          type="button"
                          disabled={!isClickable}
                          onClick={() => handleCardClick(card)}
                          className={cn(
                            'group flex flex-col justify-between rounded-2xl border p-4 text-start transition-all',
                            isClickable
                              ? 'border-line bg-white shadow-sm hover:-translate-y-0.5 hover:border-brand hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-brand'
                              : 'border-dashed border-line/80 bg-surface/30 opacity-75 dark:border-slate-800 dark:bg-slate-900/40 cursor-default'
                          )}
                        >
                          <div>
                            <div className="flex items-center justify-between">
                              <span
                                className="grid h-10 w-10 place-items-center rounded-xl bg-brand/5 text-xl group-hover:bg-brand/10 transition-colors"
                                aria-hidden
                              >
                                {card.icon}
                              </span>
                              {card.badge && (
                                <span className="rounded-full bg-brand/10 px-2.5 py-0.5 text-[10px] font-bold text-brand dark:bg-brand/20">
                                  {card.badge}
                                </span>
                              )}
                            </div>
                            <h4 className="mt-3 text-sm font-bold text-ink group-hover:text-brand dark:text-white dark:group-hover:text-brand transition-colors">
                              {title}
                            </h4>
                            <p className="mt-1 text-xs text-ink-3 dark:text-slate-400 line-clamp-2">
                              {desc}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* TAB 2: QUICK ACTIONS */}
          {activeTab === 'quickActions' && (
            <QuickActionsView
              integrationId={activeIntegrationId}
              botLabel={activeIntegration ? numberLabel(activeIntegration) : undefined}
            />
          )}
        </div>
      </div>

      {/* Bot Settings Drawer */}
      <BotSettingsDrawer
        open={settingsOpen}
        integrationId={activeIntegrationId}
        botLabel={activeIntegration ? numberLabel(activeIntegration) : undefined}
        onClose={() => setSettingsOpen(false)}
      />

      {/* Custom Variables Modal */}
      {variablesOpen && (
        <CustomVariablesModal
          open={variablesOpen}
          onClose={() => setVariablesOpen(false)}
        />
      )}

      {/* Error Logs Modal */}
      {errorLogsOpen && (
        <BotErrorLogsModal
          open={errorLogsOpen}
          integrationId={activeIntegrationId}
          botLabel={activeIntegration ? numberLabel(activeIntegration) : undefined}
          onClose={() => setErrorLogsOpen(false)}
        />
      )}

      {/* Flow Folders Modal */}
      {foldersOpen && (
        <FlowFoldersModal
          open={foldersOpen}
          onClose={() => setFoldersOpen(false)}
        />
      )}

      {/* Reset Actions Confirm Dialog */}
      {resetDialogOpen && (
        <ResetActionsConfirmDialog
          open={resetDialogOpen}
          integrationId={activeIntegrationId}
          onClose={() => setResetDialogOpen(false)}
          onReset={() => setResetDialogOpen(false)}
        />
      )}
    </div>
  );
}
